import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const fieldCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/40 outline-none transition-colors focus:border-gold";

export default function ContentManager({ title, endpoint, fields, itemTitle, itemImage }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);

  const refresh = () => api.get(`/${endpoint}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, [endpoint]);

  const openCreate = () => {
    const f = {};
    fields.forEach((fd) => (f[fd.key] = ""));
    setForm(f);
    setEditing({ id: null });
  };

  const openEdit = (item) => {
    const f = {};
    fields.forEach((fd) => (f[fd.key] = item[fd.key] ?? ""));
    setForm(f);
    setEditing(item);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(`/${endpoint}/${editing.id}`, form);
        toast.success("Modification enregistrée.");
      } else {
        await api.post(`/${endpoint}`, form);
        toast.success("Élément ajouté.");
      }
      setEditing(null);
      refresh();
    } catch {
      toast.error("Enregistrement impossible, vérifiez les champs.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cet élément ?")) return;
    await api.delete(`/${endpoint}/${id}`).catch(() => {});
    toast.success("Élément supprimé.");
    refresh();
  };

  const uploadImage = async (fieldKey, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingFor(fieldKey);
    try {
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, [fieldKey]: data.urls[0] }));
      toast.success("Photo téléversée.");
    } catch {
      toast.error("Échec du téléversement, réessayez.");
    } finally {
      setUploadingFor(null);
    }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#1C2541] p-6" data-testid={`content-manager-${endpoint}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-serif text-xl text-white">{title}</h3>
        <button
          onClick={openCreate}
          data-testid={`content-add-${endpoint}`}
          className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-[#0B132B] transition-colors hover:bg-sand"
        >
          <Plus size={13} /> Ajouter
        </button>
      </div>

      <div className="mt-4 divide-y divide-white/5">
        {items.map((item) => (
          <div key={item.id} data-testid={`content-row-${item.id}`} className="flex items-center gap-4 py-3">
            {itemImage && (
              itemImage(item) ? (
                <img src={itemImage(item)} alt="" className="h-11 w-16 shrink-0 rounded-lg object-cover" />
              ) : (
                <div className="h-11 w-16 shrink-0 rounded-lg bg-white/5" />
              )
            )}
            <p className="min-w-0 flex-1 truncate font-light text-white/85">{itemTitle(item)}</p>
            <button
              onClick={() => openEdit(item)}
              data-testid={`content-edit-${item.id}`}
              aria-label="Modifier"
              className="rounded-full border border-white/15 p-2 text-white/60 transition-colors hover:border-gold hover:text-gold"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={() => remove(item.id)}
              data-testid={`content-delete-${item.id}`}
              aria-label="Supprimer"
              className="rounded-full border border-white/15 p-2 text-white/60 transition-colors hover:border-red-400 hover:text-red-400"
            >
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="py-8 text-center font-serif italic text-white/40">Aucun élément pour le moment.</p>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl border-white/10 bg-[#1C2541] text-white" data-testid={`content-form-${endpoint}`}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-white">
              {editing?.id ? "Modifier" : "Ajouter"} — {title}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="mt-4 max-h-[70vh] space-y-3 overflow-y-auto pr-1">
            {fields.map((fd) => (
              <div key={fd.key}>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                  {fd.label}
                </label>
                {fd.textarea ? (
                  <textarea
                    rows={fd.rows || 3}
                    required
                    value={form[fd.key] || ""}
                    onChange={(e) => setForm({ ...form, [fd.key]: e.target.value })}
                    data-testid={`content-field-${fd.key}`}
                    className={fieldCls + " resize-none"}
                  />
                ) : (
                  <div className="flex gap-2">
                    <input
                      required={!fd.image}
                      value={form[fd.key] || ""}
                      onChange={(e) => setForm({ ...form, [fd.key]: e.target.value })}
                      data-testid={`content-field-${fd.key}`}
                      className={fieldCls}
                    />
                    {fd.image && (
                      <label
                        className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-xl border border-white/15 px-3 text-xs text-white/70 transition-colors hover:border-gold hover:text-gold ${uploadingFor === fd.key ? "pointer-events-none opacity-50" : ""}`}
                      >
                        <Upload size={13} /> {uploadingFor === fd.key ? "Envoi…" : "Téléverser"}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => uploadImage(fd.key, e)}
                          data-testid={`content-upload-${fd.key}`}
                        />
                      </label>
                    )}
                  </div>
                )}
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                data-testid="content-form-submit"
                className="flex-1 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-sand disabled:opacity-60"
              >
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 text-sm text-white/70 transition-colors hover:text-white"
              >
                <X size={14} /> Annuler
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
